import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ClockIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import Button from '../UI/Button';

const FOLLOW_UP_TIME_OPTIONS: string[] = (() => {
  const slots: string[] = [];
  for (let hour = 6; hour <= 21; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return slots;
})();

const formatTimeLabel = (value: string) => {
  const [h, m] = value.split(':').map((part) => Number(part));
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const applyTimeToDate = (date: Date, timeValue: string): Date => {
  const [h, m] = timeValue.split(':').map((part) => Number(part));
  const next = new Date(date);
  next.setHours(Number.isNaN(h) ? 10 : h, Number.isNaN(m) ? 0 : m, 0, 0);
  return next;
};

const parseFollowUpDate = (iso?: string | null): { date: Date | null; time: string } => {
  if (!iso) return { date: null, time: '10:00' };
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return { date: null, time: '10:00' };
  const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
  return { date: parsed, time };
};

export const formatFollowUpDate = (iso?: string | null, withTime = false) => {
  if (!iso) return 'Not scheduled';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return 'Not scheduled';
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
};

interface FollowUpDateEditorProps {
  leadId: string;
  leadName?: string;
  followUpDate?: string | null;
  status?: string;
  onUpdated?: (leadId: string, followUpDate: string) => void;
  className?: string;
  readOnly?: boolean;
  /** Controlled modal — opens picker directly without inline trigger */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

const FollowUpDateEditor: React.FC<FollowUpDateEditorProps> = ({
  leadId,
  leadName,
  followUpDate,
  onUpdated,
  className = '',
  readOnly = false,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('10:00');
  const [saving, setSaving] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  useEffect(() => {
    if (!isOpen) return;
    const { date, time } = parseFollowUpDate(followUpDate);
    if (date && date.getTime() >= Date.now()) {
      setSelectedDate(date);
      setSelectedTime(time);
      return;
    }
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 1);
    defaultDate.setHours(10, 0, 0, 0);
    setSelectedDate(defaultDate);
    setSelectedTime('10:00');
  }, [isOpen, followUpDate]);

  if (readOnly && !followUpDate) return null;

  const closeEditor = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleCalendarChange = (date: Date | null) => {
    if (!date) {
      setSelectedDate(null);
      return;
    }
    setSelectedDate(applyTimeToDate(date, selectedTime));
  };

  const handleTimeChange = (timeValue: string) => {
    setSelectedTime(timeValue);
    if (!selectedDate) return;
    setSelectedDate(applyTimeToDate(selectedDate, timeValue));
  };

  const handleSave = async () => {
    if (!selectedDate) return;
    if (selectedDate.getTime() < Date.now()) {
      toast.error('Follow-up must be in the future');
      return;
    }

    setSaving(true);
    try {
      const isoDate = selectedDate.toISOString();
      await leadService.addLeadFollowUp(leadId, isoDate);
      onUpdated?.(leadId, isoDate);
      setOpen(false);
      toast.success('Follow-up date saved');
    } catch (error) {
      console.error('Failed to save follow-up date:', error);
      toast.error('Failed to save follow-up date');
    } finally {
      setSaving(false);
    }
  };

  const modal =
    isOpen &&
    createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <button
          type="button"
          aria-label="Close follow-up editor"
          className="absolute inset-0 bg-black/50"
          onClick={closeEditor}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="follow-up-editor-title"
          className="relative w-full max-w-[400px] max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <h3 id="follow-up-editor-title" className="text-base font-semibold text-foreground">
                Schedule Follow-up{leadName ? ` — ${leadName}` : ''}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Choose a date, then pick a time.</p>
            </div>
            <button
              type="button"
              onClick={closeEditor}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="follow-up-datepicker flex justify-center">
              <DatePicker
                selected={selectedDate}
                onChange={handleCalendarChange}
                minDate={new Date()}
                inline
                calendarClassName="follow-up-calendar"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Time</label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
              >
                {FOLLOW_UP_TIME_OPTIONS.map((slot) => (
                  <option key={slot} value={slot}>
                    {formatTimeLabel(slot)}
                  </option>
                ))}
              </select>
            </div>

            {selectedDate && (
              <p className="text-xs text-muted-foreground rounded-lg bg-muted/40 px-3 py-2">
                Scheduled:{' '}
                <span className="font-medium text-foreground">
                  {selectedDate.toLocaleString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outlined" size="sm" onClick={closeEditor} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!selectedDate || saving} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  if (readOnly) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400 ${className}`}
      >
        <ClockIcon className="h-3.5 w-3.5 shrink-0" />
        <span>Follow-up: {formatFollowUpDate(followUpDate, true)}</span>
      </span>
    );
  }

  if (hideTrigger) {
    return modal;
  }

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        className={`flex w-full items-center rounded-md border border-transparent px-1.5 py-1 text-sm text-amber-600 transition-colors hover:border-amber-300/80 hover:bg-amber-50 dark:text-amber-400 dark:hover:border-amber-700/60 dark:hover:bg-amber-900/20 cursor-pointer group text-left ${className}`}
        title="Click to schedule follow-up"
        aria-label="Schedule follow-up date"
      >
        <ClockIcon className="h-4 w-4 mr-2 flex-shrink-0" />
        <span className="truncate underline-offset-2 group-hover:underline">
          Follow-up: {formatFollowUpDate(followUpDate, true)}
        </span>
        <PencilSquareIcon className="h-3.5 w-3.5 ml-1.5 flex-shrink-0 opacity-70 group-hover:opacity-100" />
      </button>
      {modal}
    </>
  );
};

export default FollowUpDateEditor;
